import { Component, computed, inject, Signal, signal, WritableSignal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';

import { StatusTranslatorService } from '@app/services/utils/status-translator.service';
import { StatusDefinition } from '@app/types/status.type';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Subject, takeUntil, debounceTime, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { StatusDialogComponent } from './status-dialog/status-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-status-viewer',
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './status-viewer.component.html',
  styleUrls: ['./status-viewer.component.scss']
})
export class StatusViewerComponent implements OnDestroy {
  private statusService = inject(StatusTranslatorService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  private allStatuses = this.statusService.getStatuses();

  searchControl = new FormControl('');
  private searchSignal: WritableSignal<string> = signal('');
  isFilteringSignal: WritableSignal<boolean> = signal(false);
  isLoadingSignal: WritableSignal<boolean> = signal(false);

  pageIndex: WritableSignal<number> = signal(0);
  pageSize: WritableSignal<number> = signal(12);

  private destroy$ = new Subject<void>();

  filteredStatusesSignal = computed(() => {
    const all = [...this.allStatuses()].sort((a, b) => a.order - b.order);
    const search = this.searchSignal().toLowerCase();

    if (!search) return all;

    return all.filter(s => {
      // Serializar todo el objeto para búsqueda profunda
      const serialized = JSON.stringify(s).toLowerCase();
      return serialized.includes(search);
    });
  });

  paginatedStatusesSignal = computed(() => {
    const items = this.filteredStatusesSignal();
    const start = this.pageIndex() * this.pageSize();
    return items.slice(start, start + this.pageSize());
  });

  constructor() {
    // Debounced search with 800ms delay
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      tap(() => this.isFilteringSignal.set(true)),
      debounceTime(800)
    ).subscribe(value => {
      this.searchSignal.set(value || '');
      this.pageIndex.set(0);
      this.isFilteringSignal.set(false);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handlePageEvent(e: PageEvent) {
    this.pageIndex.set(e.pageIndex);
    this.pageSize.set(e.pageSize);
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  openCreateDialog(): void {
    const dialogRef = this.dialog.open(StatusDialogComponent, {
      width: '600px',
      data: null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.statusService.createStatus([result]).subscribe({
          next: () => {
            this.snackBar.open('Estatus creado exitosamente', 'Cerrar', { duration: 3000 });
          },
          error: (err) => {
            this.snackBar.open(`Error al crear estatus: ${err.error?.message || err.message}`, 'Cerrar', { duration: 5000 });
          }
        });
      }
    });
  }

  openEditDialog(status: StatusDefinition): void {
    const dialogRef = this.dialog.open(StatusDialogComponent, {
      width: '600px',
      data: status
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Extract only the fields that can be updated
        const { status: _, id, deleted, ...changes } = result;

        this.statusService.updateStatus([{ id: status.id, changes }]).subscribe({
          next: () => {
            this.snackBar.open('Estatus actualizado exitosamente', 'Cerrar', { duration: 3000 });
          },
          error: (err) => {
            this.snackBar.open(`Error al actualizar estatus: ${err.error?.message || err.message}`, 'Cerrar', { duration: 5000 });
          }
        });
      }
    });
  }

  deleteStatus(status: StatusDefinition): void {
    if (confirm(`¿Está seguro de que desea eliminar el estatus "${status.nombreSurtimiento}"?`)) {
      this.statusService.deleteStatus([status.id]).subscribe({
        next: () => {
          this.snackBar.open('Estatus eliminado exitosamente', 'Cerrar', { duration: 3000 });
        },
        error: (err) => {
          this.snackBar.open(`Error al eliminar estatus: ${err.error?.message || err.message}`, 'Cerrar', { duration: 5000 });
        }
      });
    }
  }

  limpiarFiltros(): void {
    this.searchControl.setValue('');
    this.pageIndex.set(0);
  }

  forzarRefrescoCompleto(): void {
    this.isLoadingSignal.set(true);
    this.statusService.refreshStatuses().subscribe({
      next: () => {
        this.isLoadingSignal.set(false);
        this.snackBar.open('Estatus actualizados', 'Cerrar', { duration: 2000 });
      },
      error: (err) => {
        this.isLoadingSignal.set(false);
        this.snackBar.open(`Error al refrescar: ${err.message}`, 'Cerrar', { duration: 3000 });
      }
    });
  }
}